import { CSSProperties, useLayoutEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

export default function ScrollingLiveTitle({ title }: { title: string }) {
  const frame = useRef<HTMLSpanElement>(null), text = useRef<HTMLSpanElement>(null);
  const [distance, setDistance] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      const width = text.current?.getBoundingClientRect().width || 0;
      setDistance(width > (frame.current?.clientWidth || 0) + 1 ? Math.ceil(width) + 32 : 0);
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (frame.current) observer?.observe(frame.current);
    if (text.current) observer?.observe(text.current);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [title]);
  return <Frame ref={frame} title={title} data-scrolling={distance > 0} style={{
    '--title-scroll-distance': `${distance}px`, '--title-scroll-duration': `${Math.max(10, distance / 30)}s`,
  } as CSSProperties}>
    <Track $scrolling={distance > 0}>
      <span ref={text}>{title}</span>
      {distance > 0 && <span aria-hidden="true">{title}</span>}
    </Track>
  </Frame>;
}

const move = keyframes`from { transform: translateX(0); } to { transform: translateX(calc(-1 * var(--title-scroll-distance))); }`;
const Frame = styled.span`display: block; width: 100%; min-width: 0; overflow: hidden;`;
const Track = styled.span<{ $scrolling: boolean }>`
  display: flex; width: max-content; gap: 32px; white-space: nowrap;
  animation: ${({ $scrolling }) => $scrolling ? move : 'none'} var(--title-scroll-duration) linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    span[aria-hidden='true'] { display: none; }
  }
`;
